import { useEffect, useRef, useState } from 'react'
import { useSync } from '../context/SyncContext'
import { applyThemeToWebview } from '../utils/webviewTheme'

interface ExternalTabProps {
  url: string
  label: string
  tabId: string
  active: boolean
}

function isElectronRuntime(): boolean {
  if (typeof window !== 'undefined' && window.neonDeck?.isElectron) {
    return true
  }
  if (typeof navigator !== 'undefined' && /Electron/i.test(navigator.userAgent)) {
    return true
  }
  return false
}

/**
 * Electron: <webview> in-app.
 * Browser/PWA: always show a full-pane browser chrome; try iframe embed;
 * if blocked (typical on iPhone), open the live site in a system browser sheet
 * while keeping Lucky's Place chrome + reopen controls — no "Desktop-App only" dead end.
 */
export function ExternalTab({ url, label, tabId, active }: ExternalTabProps) {
  const inElectron = isElectronRuntime()
  const { settings } = useSync()
  const theme = settings.theme
  const [blocked, setBlocked] = useState(false)
  const [checking, setChecking] = useState(!inElectron)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [openedExternally, setOpenedExternally] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const webviewRef = useRef<HTMLElement | null>(null)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (inElectron) {
      setBlocked(false)
      setChecking(false)
      setLoadError(null)
      return
    }
    // Always attempt iframe first on PWA/mobile
    setChecking(true)
    setBlocked(false)
    setOpenedExternally(false)
    timerRef.current = window.setTimeout(() => {
      // Most major sites block iframes; after a short wait show the open action overlay
      // but keep the iframe underneath in case it works (e.g. some embeds).
      setChecking(false)
      setBlocked(true)
    }, 1200)
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [url, inElectron, active])

  useEffect(() => {
    if (!inElectron) return
    const el = webviewRef.current
    if (!el) return

    const onFail = (event: Event) => {
      const detail = event as Event & {
        errorCode?: number
        errorDescription?: string
        isMainFrame?: boolean
      }
      if (detail.errorCode === -3) return
      if (detail.isMainFrame === false) return
      setLoadError(
        detail.errorDescription ||
          `Laden fehlgeschlagen (Code ${detail.errorCode ?? '?'})`,
      )
    }

    const onStart = () => setLoadError(null)
    const onReady = () => {
      void applyThemeToWebview(el, theme, tabId)
    }

    el.addEventListener('did-fail-load', onFail)
    el.addEventListener('did-start-loading', onStart)
    el.addEventListener('dom-ready', onReady)
    el.addEventListener('did-finish-load', onReady)
    void applyThemeToWebview(el, theme, tabId)

    return () => {
      el.removeEventListener('did-fail-load', onFail)
      el.removeEventListener('did-start-loading', onStart)
      el.removeEventListener('dom-ready', onReady)
      el.removeEventListener('did-finish-load', onReady)
    }
  }, [inElectron, url, theme, tabId])

  const openInAppBrowser = () => {
    // On iPhone PWA this opens Safari's in-app / browser sheet; user can swipe back to Lucky's Place
    const win = window.open(url, '_blank', 'noopener,noreferrer')
    setOpenedExternally(true)
    if (!win) {
      // popup blocked — navigate top-level as last resort
      window.location.assign(url)
    }
  }

  if (inElectron) {
    return (
      <div
        className="external-tab external-tab--webview"
        style={{ display: active ? 'flex' : 'none' }}
        aria-hidden={!active}
      >
        <webview
          ref={(node) => {
            webviewRef.current = node
          }}
          className="external-tab__webview"
          src={url}
          partition={`persist:${tabId}`}
          allowpopups={true}
          style={{ width: '100%', height: '100%', border: 0 }}
        />
        {loadError && active && (
          <div className="external-tab__error" role="alert">
            <p>
              {label}: {loadError}
            </p>
            <button
              type="button"
              className="neon-btn neon-btn--ghost"
              onClick={() => {
                setLoadError(null)
                const el = webviewRef.current as
                  | (HTMLElement & { reload?: () => void })
                  | null
                el?.reload?.()
              }}
            >
              Erneut laden
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!active) return null

  return (
    <div className="external-tab external-tab--pwa">
      <iframe
        ref={iframeRef}
        className="external-tab__iframe"
        src={url}
        title={label}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
        referrerPolicy="no-referrer-when-downgrade"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      />

      {(blocked || checking || openedExternally) && (
        <div className={`fallback-panel${openedExternally ? ' fallback-panel--opened' : ''}`}>
          <div className="fallback-panel__grid" aria-hidden />
          <div className="fallback-panel__content">
            <div className="fallback-panel__badge">{checking ? 'LADE…' : 'IN-APP'}</div>
            <h2 className="fallback-panel__title">{label}</h2>
            <p className="fallback-panel__text">
              {openedExternally
                ? `${label} wurde geöffnet. Kehre danach hierher zurück (App-Umschalter oder Home-Bildschirm).`
                : `${label} lässt sich auf dem iPhone oft nicht direkt einbetten (Sicherheitsregel der Seite). Tippe auf Öffnen — die Seite erscheint in der Browser-Ansicht, Lucky's Place bleibt installiert.`}
            </p>
            <p className="fallback-panel__url mono">{url}</p>
            <button
              type="button"
              className="neon-btn neon-btn--primary"
              onClick={openInAppBrowser}
            >
              {openedExternally ? `${label} erneut öffnen` : `${label} öffnen`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
